/**
 * @name umi 的路由配置
 * @description 只支持 path,component,routes,redirect,wrappers,name,icon 的配置
 * @param path  path 只支持两种占位符配置，第一种是动态参数 :id 的形式，第二种是 * 通配符，通配符只能出现路由字符串的最后。
 * @param component 配置 location 和 path 匹配后用于渲染的 React 组件路径。可以是绝对路径，也可以是相对路径，如果是相对路径，会从 src/pages 开始找起。
 * @param routes 配置子路由，通常在需要为多个路径增加 layout 组件时使用。
 * @param redirect 配置路由跳转
 * @param wrappers 配置路由组件的包装组件，通过包装组件可以为当前的路由组件组合进更多的功能。 比如，可以用于路由级别的权限校验
 * @param name 配置路由的标题，默认读取国际化文件 menu.ts 中 menu.xxxx 的值，如配置 name 为 login，则读取 menu.ts 中 menu.login 的取值作为标题
 * @param icon 配置路由的图标，取值参考 https://ant.design/components/icon-cn， 注意去除风格后缀和大小写，如想要配置图标为 <StepBackwardOutlined /> 则取值应为 stepBackward 或 StepBackward，如想要配置图标为 <UserOutlined /> 则取值应为 user 或者 User
 * @doc https://umijs.org/docs/guides/routes
 */
export default [
  {
    path: '/reader-feedback/preview',
    layout: false,
    component: './reader-feedback/preview',
  },
  {
    path: '/maintenance/reader-bugs/:token',
    layout: false,
    component: './maintenance/reader-bugs/public',
  },
  {
    path: '/user',
    layout: false,
    routes: [
      {
        name: 'login',
        path: '/user/login',
        component: './user/login',
      },
      {
        name: 'selectProduct',
        path: '/user/select-product',
        component: './user/select-product',
      },
    ],
  },
  {
    path: '/welcome',
    redirect: '/dashboard/workplace',
    hideInMenu: true,
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    icon: 'smile',
    access: 'canViewWorkbench',
    permission: {
      code: 'dashboard:read',
      name: '进入数据看板',
      module: 'dashboard',
      description: '进入数据看板主菜单',
    },
    routes: [
      {
        path: '/dashboard',
        redirect: '/dashboard/workplace',
      },
      {
        path: '/dashboard/workplace',
        name: 'workplace',
        access: 'canViewWorkbench',
        permission: {
          code: 'dashboard.workplace:read',
          name: '查看工作台',
          module: 'dashboard',
          description: '查看工作台和管理端总览数据',
        },
        actions: [
          {
            code: 'dashboard.refresh:read',
            name: '刷新数据看板按钮',
            module: 'dashboard',
            description: '刷新工作台数据看板',
          },
        ],
        component: './Welcome',
      },
      {
        path: '/dashboard/comic-rankings',
        name: 'comicRankings',
        access: 'canViewDashboard',
        permission: {
          code: 'dashboard.comic_rankings:read',
          name: '查看漫画榜单',
          module: 'dashboard',
          description: '查看漫画详情访问次数和阅读深度榜单',
        },
        actions: [
          {
            code: 'dashboard.comic_rankings.refresh:read',
            name: '刷新漫画榜单',
            module: 'dashboard',
          },
          {
            code: 'dashboard.comic_rankings.export:read',
            name: '导出漫画榜单',
            module: 'dashboard',
          },
          {
            code: 'dashboard.comic_rankings.columns:read',
            name: '配置榜单列',
            module: 'dashboard',
          },
        ],
        component: './dashboard/comic-rankings',
      },
      {
        path: '/dashboard/package-metrics',
        name: 'packageMetrics',
        access: 'canViewPackageMetrics',
        permission: {
          code: 'dashboard.package_metrics:read',
          name: '查看按包统计',
          module: 'dashboard',
          description: '查看按包聚合的活跃用户和请求量',
        },
        actions: [
          {
            code: 'dashboard.package_metrics.refresh:read',
            name: '刷新按包统计',
            module: 'dashboard',
          },
        ],
        component: './dashboard/package-metrics',
      },
    ],
  },
  {
    path: '/comics',
    name: 'comics',
    icon: 'book',
    access: 'canReadContent',
    permission: [
      {
        code: 'content:read',
        name: '进入漫画管理',
        module: 'content',
        description: '进入漫画管理主菜单',
      },
    ],
    actions: [
      {
        code: 'content:write',
        name: '漫画管理通用编辑按钮',
        module: 'content',
        description: '创建、修改、删除漫画和章节',
      },
    ],
    routes: [
      {
        path: '/comics',
        name: 'list',
        access: 'canReadContent',
        permission: {
          code: 'content.comics.list:read',
          name: '查看漫画列表',
          module: 'content',
          description: '查看漫画列表页面',
        },
        actions: [
          {
            code: 'content.comics.list.tag_overrides.detail:read',
            name: '查看漫画手动标签规则',
            module: 'content',
          },
          {
            code: 'content.comics.list.tag_review.update:write',
            name: '确认或撤销漫画标签不适用',
            module: 'content',
          },
          {
            code: 'content.comics.list.tag_overrides.batch:write',
            name: '批量添加漫画标签',
            module: 'content',
          },
          {
            code: 'content.comics.list.tag_overrides.update:write',
            name: '保存漫画手动标签规则',
            module: 'content',
          },
          {
            code: 'content.comics.list.edit:write',
            name: '编辑漫画按钮',
            module: 'content',
          },
          {
            code: 'content.comics.list.chapters:write',
            name: '章节管理按钮',
            module: 'content',
          },
          {
            code: 'content.comics.list.translation_eval:read',
            name: '翻译评估按钮',
            module: 'content',
          },
          {
            code: 'content.comics.list.sync:write',
            name: '同步漫画按钮',
            module: 'content',
          },
          {
            code: 'content.comics.list.batch:write',
            name: '批量操作按钮',
            module: 'content',
          },
          {
            code: 'content.comics.list.preview:read',
            name: '预览详情按钮',
            module: 'content',
          },
        ],
        component: './comics',
      },
      {
        path: '/comics/recommended',
        redirect: '/growth-config/app-decor-editor?tab=recommended',
        hideInMenu: true,
      },
      {
        path: '/comics/works',
        name: 'works',
        access: 'canReadContent',
        permission: {
          code: 'content.comics.works:read',
          name: '查看聚合漫画',
          module: 'content',
          description: '查看聚合漫画页面',
        },
        actions: [
          {
            code: 'content.comics.works.manage:write',
            name: '管理聚合漫画按钮',
            module: 'content',
          },
          {
            code: 'content.comics.works.prepare_cover:write',
            name: '补齐漫画封面',
            module: 'content',
            description: '允许同步任务探测封面并补齐缺失的公开 WebP 图片',
          },
          {
            code: 'content.comics.works.refresh:read',
            name: '刷新聚合漫画按钮',
            module: 'content',
          },
          {
            code: 'content.comics.works.bind:write',
            name: '绑定漫画按钮',
            module: 'content',
          },
          {
            code: 'content.comics.works.save:write',
            name: '保存聚合漫画按钮',
            module: 'content',
          },
        ],
        component: './comics/works',
      },
      {
        path: '/comics/exports',
        name: 'exports',
        access: 'canReadContent',
        permission: {
          code: 'content.comics.exports:read',
          name: '查看导出管理',
          module: 'content',
          description: '查看导出管理页面',
        },
        actions: [
          {
            code: 'content.comics.exports.download:read',
            name: '下载导出文件按钮',
            module: 'content',
          },
        ],
        component: './comics/exports',
      },
      {
        path: '/comics/reviews',
        name: '漫画审核',
        access: 'canReadComicReviews',
        routes: [
          { path: '/comics/reviews', redirect: '/comics/reviews/pending' },
          {
            path: '/comics/reviews/pending',
            name: '待审核',
            hideInMenu: true,
            component: './comics/reviews',
            access: 'canReadComicReviews',
            permission: {
              code: 'content.comic_review.pending:read',
              name: '查看待审核列表',
              module: 'content',
            },
            actions: [
              {
                code: 'content.comic_review.pending.detail:read',
                name: '查看审核详情',
                module: 'content',
              },
              {
                code: 'content.comic_review.pending.update:write',
                name: '保存章节进度和图片原因',
                module: 'content',
              },
              {
                code: 'content.comic_review.pending.approve:write',
                name: '审核通过并导入',
                module: 'content',
              },
              {
                code: 'content.comic_review.pending.reject:write',
                name: '审核不通过',
                module: 'content',
              },
              {
                code: 'content.comic_review.pending.upload:write',
                name: '直传审核 JSON',
                module: 'content',
              },
              {
                code: 'content.comic_review.pending.columns:read',
                name: '配置列表列',
                module: 'content',
              },
              {
                code: 'content.comic_review.pending.refresh:read',
                name: '刷新待审核列表',
                module: 'content',
              },
            ],
          },
          {
            path: '/comics/reviews/approved',
            name: '已通过',
            hideInMenu: true,
            component: './comics/reviews',
            access: 'canReadComicReviews',
            permission: {
              code: 'content.comic_review.approved:read',
              name: '查看审核通过列表',
              module: 'content',
            },
            actions: [
              {
                code: 'content.comic_review.approved.detail:read',
                name: '查看通过记录详情',
                module: 'content',
              },
              {
                code: 'content.comic_review.approved.columns:read',
                name: '配置列表列',
                module: 'content',
              },
              {
                code: 'content.comic_review.approved.refresh:read',
                name: '刷新通过列表',
                module: 'content',
              },
            ],
          },
          {
            path: '/comics/reviews/rejected',
            name: '未通过',
            hideInMenu: true,
            component: './comics/reviews',
            access: 'canReadComicReviews',
            permission: {
              code: 'content.comic_review.rejected:read',
              name: '查看审核不通过列表',
              module: 'content',
            },
            actions: [
              {
                code: 'content.comic_review.rejected.detail:read',
                name: '查看不通过记录详情',
                module: 'content',
              },
              {
                code: 'content.comic_review.rejected.columns:read',
                name: '配置列表列',
                module: 'content',
              },
              {
                code: 'content.comic_review.rejected.refresh:read',
                name: '刷新不通过列表',
                module: 'content',
              },
            ],
          },
        ],
      },
      {
        path: '/comics/display-rules',
        name: 'displayRules',
        access: 'canReadContent',
        permission: {
          code: 'content.comics.display_rules:read',
          name: '查看 displayRules',
          module: 'content',
          description: '查看漫画展示规则页面',
        },
        actions: [
          {
            code: 'content.comics.display_rules.create:write',
            name: '新建规则按钮',
            module: 'content',
          },
          {
            code: 'content.comics.display_rules.edit:write',
            name: '编辑规则按钮',
            module: 'content',
          },
          {
            code: 'content.comics.display_rules.delete:write',
            name: '删除规则按钮',
            module: 'content',
          },
          {
            code: 'content.comics.display_rules.affected:read',
            name: '查看影响漫画按钮',
            module: 'content',
          },
        ],
        component: './comics/display-rules',
      },
      {
        path: '/comics/report-analysis',
        name: 'reportAnalysis',
        access: 'canReadContent',
        permission: {
          code: 'content.comics.report_analysis:read',
          name: '查看报表分析',
          module: 'content',
          description: '查看漫画报表分析页面',
        },
        actions: [
          {
            code: 'content.comics.report_analysis.refresh:read',
            name: '刷新报表按钮',
            module: 'content',
          },
          {
            code: 'content.comics.report_analysis.download:read',
            name: '下载报表按钮',
            module: 'content',
          },
          {
            code: 'content.comics.report_analysis.snapshot:write',
            name: '生成快照按钮',
            module: 'content',
          },
          {
            code: 'content.comics.report_analysis.detail:read',
            name: '查看详情按钮',
            module: 'content',
          },
        ],
        component: './comics/report-analysis',
      },
      {
        path: '/comics/retention-analysis',
        name: 'retentionAnalysis',
        access: 'canReadContent',
        permission: {
          code: 'content.comics.retention_analysis:read',
          name: '查看留存分析',
          module: 'content',
          description: '查看漫画留存分析页面',
        },
        actions: [
          {
            code: 'content.retention.snapshot:write',
            name: '生成留存快照按钮',
            module: 'content',
            description: '生成当天留存快照按钮',
          },
          {
            code: 'content.retention.refresh:read',
            name: '刷新留存分析按钮',
            module: 'content',
            description: '刷新留存分析数据',
          },
        ],
        routes: [
          {
            path: '/comics/retention-analysis/user',
            name: 'retentionUser',
            hideInMenu: true,
            permission: {
              code: 'content.retention.user:read',
              name: '用户留存 Tab',
              module: 'content',
              description: '用户留存分析标签页',
            },
          },
          {
            path: '/comics/retention-analysis/user-comic',
            name: 'retentionComic',
            hideInMenu: true,
            permission: {
              code: 'content.retention.user_comic:read',
              name: '漫画阅读留存 Tab',
              module: 'content',
              description: '漫画阅读留存分析标签页',
            },
          },
        ],
        component: './comics/retention-analysis',
      },
      {
        path: '/comics/spider-sync',
        name: 'spiderSync',
        access: 'canReadContent',
        permission: {
          code: 'content.comics.spider_sync:read',
          name: '查看 Spider 同步',
          module: 'content',
          description: '查看 Spider 同步页面',
        },
        actions: [
          {
            code: 'content.comics.spider_sync.download_source:read',
            name: '下载源文件按钮',
            module: 'content',
          },
          {
            code: 'content.comics.spider_sync.force_sync:write',
            name: '强制同步按钮',
            module: 'content',
          },
          {
            code: 'content.comics.spider_sync.cancel:write',
            name: '取消任务按钮',
            module: 'content',
          },
        ],
        component: './comics/spider-sync',
      },
      {
        path: '/comics/edit/:id',
        name: 'edit',
        access: 'canWriteContent',
        permission: {
          code: 'content.comics.edit:write',
          name: '编辑漫画页面',
          module: 'content',
          description: '进入编辑漫画页面并保存',
        },
        component: './comics/edit',
        hideInMenu: true,
      },
      {
        path: '/comics/:comicId/chapters',
        name: 'chapters',
        access: 'canWriteContent',
        permission: {
          code: 'content.comics.chapters:write',
          name: '章节管理页面',
          module: 'content',
          description: '进入章节管理页面',
        },
        actions: [
          {
            code: 'content.comics.chapters.create:write',
            name: '新建章节按钮',
            module: 'content',
          },
          {
            code: 'content.comics.chapters.edit:write',
            name: '编辑章节按钮',
            module: 'content',
          },
          {
            code: 'content.comics.chapters.delete_page:write',
            name: '删除章节页按钮',
            module: 'content',
          },
          {
            code: 'content.comics.chapters.upload_page:write',
            name: '上传章节页按钮',
            module: 'content',
          },
          {
            code: 'content.comics.chapters.preview_page:read',
            name: '预览章节页按钮',
            module: 'content',
          },
          {
            code: 'content.comics.chapters.set_cover:write',
            name: '设置封面按钮',
            module: 'content',
          },
        ],
        component: './comics/chapters',
        hideInMenu: true,
      },
      {
        path: '/comics/:comicId/chapters/:chapterId',
        name: 'chapterEdit',
        access: 'canWriteContent',
        permission: {
          code: 'content.comics.chapter_edit:write',
          name: '章节编辑页面',
          module: 'content',
          description: '进入章节编辑页面并保存',
        },
        component: './comics/chapters',
        hideInMenu: true,
      },
    ],
  },
  {
    path: '/tags',
    name: 'tags',
    icon: 'tags',
    access: 'canReadTags',
    permission: [
      {
        code: 'tags:read',
        name: '进入标签管理',
        module: 'tags',
        description: '进入标签管理主菜单',
      },
    ],
    actions: [
      {
        code: 'tags:write',
        name: '标签管理通用编辑按钮',
        module: 'tags',
        description: '创建、修改、删除标签',
      },
    ],
    routes: [
      {
        path: '/tags',
        redirect: '/tags/source',
      },
      {
        path: '/tags/source',
        name: 'source',
        access: 'canReadTags',
        permission: {
          code: 'tags.source:read',
          name: '查看标签',
          module: 'tags',
          description: '查看统一标签列表',
        },
        actions: [
          {
            code: 'tags.source.create:write',
            name: '新建标签按钮',
            module: 'tags',
          },
          {
            code: 'tags.source.edit:write',
            name: '编辑标签按钮',
            module: 'tags',
          },
          {
            code: 'tags.source.delete:write',
            name: '删除标签按钮',
            module: 'tags',
          },
          {
            code: 'tags.source.detail:read',
            name: '查看标签详情',
            module: 'tags',
          },
        ],
        component: './tags',
      },
      {
        path: '/tags/master',
        redirect: '/tags/source',
        hideInMenu: true,
      },
      {
        path: '/tags/translations',
        hideInMenu: true,
        name: 'translations',
        access: 'canReadTags',
        permission: {
          code: 'tags.translations:read',
          name: '查看标签翻译',
          module: 'tags',
          description: '查看标签翻译页面',
        },
        actions: [
          {
            code: 'tags.translations.refresh:read',
            name: '刷新翻译按钮',
            module: 'tags',
          },
          {
            code: 'tags.translations.save:write',
            name: '保存翻译按钮',
            module: 'tags',
          },
        ],
        component: './tags/translations',
      },
    ],
  },
  {
    path: '/logs',
    name: 'logs',
    icon: 'history',
    access: 'canReadLogs',
    permission: [
      {
        code: 'logs:read',
        name: '进入 App 用户',
        module: 'logs',
        description: '进入 App 用户主菜单',
      },
    ],
    actions: [
      {
        code: 'logs:write',
        name: 'App 用户通用策略按钮',
        module: 'logs',
        description: '添加或移除白名单设备',
      },
    ],
    routes: [
      {
        path: '/logs',
        redirect: '/logs/app-users',
      },
      {
        path: '/logs/blacklist',
        redirect: '/logs/app-users',
      },
      {
        path: '/logs/app-users',
        name: 'blacklist',
        access: 'canReadLogsAppUsers',
        permission: {
          code: 'logs.app_users:read',
          name: '查看 App 用户',
          module: 'logs',
          description: '查看 App 用户页面',
        },
        actions: [
          {
            code: 'logs.app_users.user_settings.detail:read',
            name: '查看用户设置',
            module: 'logs',
          },
          {
            code: 'logs.app_users.user_settings.update:write',
            name: '更新用户设置',
            module: 'logs',
          },
          {
            code: 'logs.app_users.refresh:read',
            name: '刷新用户按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.blacklist:write',
            name: '拉黑用户按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.whitelist:write',
            name: '加入白名单按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.toggle_google:write',
            name: '切换 Google 状态按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.toggle_apple:write',
            name: '切换 Apple 状态按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.ad_user:write',
            name: '设置广告用户按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.policy_reset:write',
            name: '清理策略判断按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.fcm_message:write',
            name: '发送 FCM 消息按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.comic_push_test:write',
            name: '模拟漫画推送按钮',
            module: 'logs',
          },
          {
            code: 'logs.app_users.setting_tags:read',
            name: '查看用户设置标签',
            module: 'logs',
          },
          {
            code: 'logs.app_users.setting_tags:write',
            name: '管理用户设置标签',
            module: 'logs',
          },
        ],
        component: './blacklist',
      },
      {
        path: '/logs/black-records',
        name: 'blackRecords',
        access: 'canReadLogsBlackRecords',
        permission: {
          code: 'logs.black_records:read',
          name: '查看黑名单记录',
          module: 'logs',
          description: '查看黑名单记录页面',
        },
        actions: [
          {
            code: 'logs.black_records.refresh:read',
            name: '刷新记录按钮',
            module: 'logs',
          },
          {
            code: 'logs.black_records.export:read',
            name: '导出记录按钮',
            module: 'logs',
          },
        ],
        component: './access_log',
      },
      {
        path: '/logs/search-records',
        name: 'searchRecords',
        access: 'canReadLogsSearchRecords',
        permission: {
          code: 'logs.search_records:read',
          name: '查看搜索记录',
          module: 'logs',
          description: '查看客户端漫画搜索关键词和命中结果',
        },
        component: './search-records',
      },
      {
        path: '/logs/comic-push-deliveries',
        name: 'comicPushDeliveries',
        access: 'canReadLogsComicPushDeliveries',
        permission: {
          code: 'logs.comic_push_deliveries:read',
          name: '查看漫画推送记录',
          module: 'logs',
          description: '查看自动和模拟漫画推送发送记录',
        },
        component: './blacklist/comic-push-deliveries',
      },
      {
        path: '/logs/whitelist',
        name: 'whitelist',
        access: 'canReadLogsWhitelist',
        permission: {
          code: 'logs.whitelist:read',
          name: '查看白名单',
          module: 'logs',
          description: '查看白名单页面',
        },
        routes: [
          {
            path: '/logs/whitelist/list',
            name: 'whitelistList',
            hideInMenu: true,
            permission: [
              {
                code: 'logs.whitelist.list:read',
                name: '白名单列表 Tab',
                module: 'logs',
                description: '白名单列表标签页',
              },
            ],
            actions: [
              {
                code: 'logs.whitelist.list.create:write',
                name: '新增白名单按钮',
                module: 'logs',
                description: '新增白名单记录',
              },
              {
                code: 'logs.whitelist.list.edit:write',
                name: '编辑白名单按钮',
                module: 'logs',
              },
              {
                code: 'logs.whitelist.list.delete:write',
                name: '删除白名单按钮',
                module: 'logs',
              },
            ],
          },
          {
            path: '/logs/whitelist/policy-gateway-test-routes',
            name: 'whitelistPolicyGateway',
            hideInMenu: true,
            permission: {
              code: 'logs.whitelist.policy_gateway_test_routes:read',
              name: '用户转发 Tab',
              module: 'logs',
              description: '用户转发测试路由标签页',
            },
            actions: [
              {
                code: 'logs.whitelist.policy_gateway_test_routes.create:write',
                name: '新增转发路由按钮',
                module: 'logs',
              },
              {
                code: 'logs.whitelist.policy_gateway_test_routes.edit:write',
                name: '编辑转发路由按钮',
                module: 'logs',
              },
              {
                code: 'logs.whitelist.policy_gateway_test_routes.delete:write',
                name: '删除转发路由按钮',
                module: 'logs',
              },
              {
                code: 'logs.whitelist.policy_gateway_test_routes.refresh:read',
                name: '刷新转发路由按钮',
                module: 'logs',
              },
            ],
          },
        ],
        component: './whitelist',
      },
      {
        path: '/logs/user-policies',
        name: 'userPolicies',
        access: 'canReadLogsUserPolicies',
        permission: {
          code: 'logs.user_policies:read',
          name: '查看用户策略',
          module: 'logs',
          description: '查看用户策略页面',
        },
        routes: [
          {
            path: '/logs/user-policies/honeypot',
            name: 'userPoliciesHoneypot',
            hideInMenu: true,
            permission: [
              {
                code: 'logs.user_policies.honeypot:read',
                name: '蜜罐策略 Tab',
                module: 'logs',
                description: '蜜罐策略页标签页',
              },
            ],
            actions: [
              {
                code: 'logs.user_policies.honeypot:write',
                name: '编辑蜜罐策略按钮',
                module: 'logs',
              },
              {
                code: 'logs.user_policies.honeypot.save:write',
                name: '保存蜜罐策略按钮',
                module: 'logs',
                description: '保存蜜罐策略',
              },
              {
                code: 'logs.user_policies.honeypot.reset:write',
                name: '重置蜜罐策略按钮',
                module: 'logs',
              },
            ],
          },
          {
            path: '/logs/user-policies/registration',
            name: 'userPoliciesRegistration',
            hideInMenu: true,
            permission: [
              {
                code: 'logs.user_policies.registration:read',
                name: '注册策略 Tab',
                module: 'logs',
                description: '注册策略页标签页',
              },
            ],
            actions: [
              {
                code: 'logs.user_policies.registration.create:write',
                name: '新增注册策略按钮',
                module: 'logs',
                description: '新增注册策略',
              },
              {
                code: 'logs.user_policies.registration.edit:write',
                name: '编辑注册策略按钮',
                module: 'logs',
              },
              {
                code: 'logs.user_policies.registration.delete:write',
                name: '删除注册策略按钮',
                module: 'logs',
              },
            ],
          },
          {
            path: '/logs/user-policies/trusted',
            name: 'userPoliciesTrusted',
            hideInMenu: true,
            permission: [
              {
                code: 'logs.user_policies.trusted:read',
                name: '高活跃用户策略 Tab',
                module: 'logs',
                description: '高活跃用户策略页标签页',
              },
            ],
            actions: [
              {
                code: 'logs.user_policies.trusted:write',
                name: '编辑高活跃策略按钮',
                module: 'logs',
              },
              {
                code: 'logs.user_policies.trusted.save:write',
                name: '保存高活跃策略按钮',
                module: 'logs',
                description: '保存高活跃用户策略',
              },
              {
                code: 'logs.user_policies.trusted.reset:write',
                name: '重置高活跃策略按钮',
                module: 'logs',
              },
            ],
          },
        ],
        component: './blacklist/user-policies',
      },
      {
        path: '/logs/user-policy-hits',
        name: 'userPolicyHits',
        access: 'canReadLogsUserPolicyHits',
        permission: {
          code: 'logs.user_policy_hits:read',
          name: '查看策略命中记录',
          module: 'logs',
          description: '查看用户策略命中记录页面',
        },
        actions: [
          {
            code: 'logs.user_policy_hits.refresh:read',
            name: '刷新命中记录按钮',
            module: 'logs',
          },
          {
            code: 'logs.user_policy_hits.export:read',
            name: '导出命中记录按钮',
            module: 'logs',
          },
        ],
        component: './blacklist/user-policy-hits',
      },
    ],
  },
  {
    path: '/audit-config',
    hideInMenu: true,
    redirect: '/version-management/packages',
  },
  {
    path: '/version-management',
    name: 'versionManagement',
    icon: 'appstore',
    access: 'canReadVersionManagement',
    permission: [
      {
        code: 'version_management:read',
        name: '进入版本管理',
        module: 'version_management',
        description: '进入版本管理主菜单',
      },
    ],
    actions: [
      {
        code: 'version_management:write',
        name: '版本管理通用编辑按钮',
        module: 'version_management',
        description: '编辑版本审核、应用管理和 IP 池管理',
      },
    ],
    routes: [
      {
        path: '/version-management',
        redirect: '/version-management/packages',
      },
      {
        path: '/version-management/version',
        redirect: '/version-management/packages',
        hideInMenu: true,
      },
      {
        path: '/version-management/ips',
        name: 'ips',
        access: 'canReadVersionManagementIps',
        permission: [
          {
            code: 'version_management.ips:read',
            name: '查看 IP 池管理',
            module: 'version_management',
            description: '查看版本管理 IP 池页',
          },
        ],
        actions: [
          {
            code: 'version_management.ips:write',
            name: '编辑 IP 池管理按钮',
            module: 'version_management',
            description: '编辑版本管理 IP 池页',
          },
          {
            code: 'version_management.ips.create:write',
            name: '新增 IP 池按钮',
            module: 'version_management',
          },
          {
            code: 'version_management.ips.delete:write',
            name: '删除 IP 池按钮',
            module: 'version_management',
          },
        ],
        component: './system/version-management/ips',
      },
      {
        path: '/version-management/packages',
        name: 'packages',
        order: 0,
        access: 'canReadVersionManagementPackages',
        permission: {
          code: 'version_management.packages:read',
          name: '查看包管理',
          module: 'version_management',
          description: '查看应用商店包上架状态管理',
        },
        actions: [
          {
            code: 'version_management.packages:write',
            name: '编辑包管理按钮',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.create:write',
            name: '新增包按钮',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.delete:write',
            name: '删除包按钮',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.detail:read',
            name: '查看包详情',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.update:write',
            name: '更新包资料',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.tenant.update:write',
            name: '切换包所属租户',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.store_probe:read',
            name: '探测商店地址',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.versions:read',
            name: '查看包版本管理',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.versions:write',
            name: '编辑包版本管理',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.version_create:write',
            name: '添加审核版本',
            module: 'version_management',
          },
          {
            code: 'audit_config.permanent_black_clear:write',
            name: '清理审核版本永黑记录',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.onboarding.detail:read',
            name: '查看首次接入',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.onboarding.update:write',
            name: '保存接入资料及更换域名',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.onboarding.verify:write',
            name: '验证域名连通性',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.domain_quote:read',
            name: '查询域名报价',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.documents:read',
            name: '预览及复制接入文档',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.account.create:write',
            name: '创建后台账号',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.account.link:write',
            name: '关联已有客户端账号',
            module: 'version_management',
          },
          {
            code: 'version_management.packages.credentials:read',
            name: '复制含账号密码的开发资料',
            module: 'version_management',
          },
        ],
        component: './system/version-management/packages',
      },
    ],
  },
  {
    path: '/growth-config',
    name: 'growthConfig',
    icon: 'rocket',
    access: 'canReadGrowth',
    permission: [
      {
        code: 'growth:read',
        name: '进入增长配置',
        module: 'growth',
        description: '进入增长配置主菜单',
      },
    ],
    actions: [
      {
        code: 'growth:write',
        name: '增长配置通用编辑按钮',
        module: 'growth',
        description: '兼容旧账号组，允许编辑全部增长配置页面',
      },
      { code: 'growth.base:read', name: '查看增长基础配置', module: 'growth' },
      { code: 'growth.base:write', name: '编辑增长基础配置', module: 'growth' },
      {
        code: 'growth.marketing_languages:read',
        name: '查看营销语言配置',
        module: 'growth',
      },
      {
        code: 'growth.marketing_languages:write',
        name: '编辑营销语言配置',
        module: 'growth',
      },
      { code: 'assets:write', name: '上传资源按钮', module: 'assets' },
    ],
    routes: [
      {
        path: '/growth-config',
        component: './growth-config/entry',
        hideInMenu: true,
      },
      {
        path: '/growth-config/ad-slots',
        name: 'adConfig',
        access: 'canReadGrowthAdSlots',
        permission: [
          {
            code: 'growth.ad_slots:read',
            name: '查看广告配置',
            module: 'growth',
            description: '进入广告配置菜单',
          },
        ],
        actions: [
          {
            code: 'growth.ad_slots:write',
            name: '编辑广告位配置按钮',
            module: 'growth',
            description: '编辑广告位配置页',
          },
          {
            code: 'growth.ad_slots.reload:read',
            name: '刷新广告位按钮',
            module: 'growth',
          },
        ],
        routes: [
          {
            path: '/growth-config/ad-slots',
            name: 'adManagement',
            access: 'canReadAdManagement',
            permission: {
              code: 'growth.ad_slots.management:read',
              name: '广告管理',
              module: 'growth',
              description: '广告组与全局广告配置管理页',
            },
            actions: [
              {
                code: 'growth.ad_slots.delivery.users:read',
                name: '查看行内下发用户',
                module: 'growth',
                description: '查看广告组和全局广告配置对应的下发用户',
              },
              {
                code: 'growth.ad_slots.delivery.changes:read',
                name: '查看行内变更记录',
                module: 'growth',
                description: '查看广告组和全局广告配置对应的变更记录',
              },
            ],
            routes: [
              {
                path: '/growth-config/ad-slots/tab',
                name: 'adSlotsConfig',
                hideInMenu: true,
                access: 'canReadAdGroups',
                permission: {
                  code: 'growth.ad_slots.config:read',
                  name: '广告组 Tab',
                  module: 'growth',
                  description: '广告组配置标签页',
                },
                actions: [
                  { code: 'growth.ad_slots.config.coverage:read', name: '检查广告配置覆盖', module: 'growth' },
                  {
                    code: 'growth.ad_slots.config.create:write',
                    name: '新增/复制广告组按钮',
                    module: 'growth',
                    description: '新增广告组；复制配置还需查看广告组详情权限',
                  },
                  {
                    code: 'growth.ad_slots.config.detail:read',
                    name: '查看广告组详情按钮',
                    module: 'growth',
                  },
                  {
                    code: 'growth.ad_slots.config.update:write',
                    name: '更新广告组按钮',
                    module: 'growth',
                  },
                  {
                    code: 'growth.ad_slots.config.delete:write',
                    name: '删除广告组按钮',
                    module: 'growth',
                  },
                  {
                    code: 'growth.ad_slots.config.column:read',
                    name: '列配置按钮',
                    module: 'growth',
                  },
                ],
              },
              {
                path: '/growth-config/ad-slots/global-tab',
                name: 'adGlobalConfigs',
                hideInMenu: true,
                access: 'canReadGlobalAdConfigs',
                permission: {
                  code: 'growth.ad_slots.global:read',
                  name: '全局广告配置 Tab',
                  module: 'growth',
                  description: '全局广告配置标签页',
                },
                actions: [
                  { code: 'growth.ad_slots.global.coverage:read', name: '检查广告配置覆盖', module: 'growth' },
                  {
                    code: 'growth.ad_slots.global.create:write',
                    name: '新增/复制全局广告配置按钮',
                    module: 'growth',
                    description: '新增全局广告配置，或复制现有配置为新增草稿',
                  },
                  {
                    code: 'growth.ad_slots.global.update:write',
                    name: '更新全局广告配置按钮',
                    module: 'growth',
                  },
                  {
                    code: 'growth.ad_slots.global.delete:write',
                    name: '删除全局广告配置按钮',
                    module: 'growth',
                  },
                ],
              },
            ],
            component: './growth-config/ad-slots',
          },
          {
            path: '/growth-config/ad-slots/ad-id-management',
            name: 'adIds',
            access: 'canReadAdIds',
            permission: {
              code: 'growth.ad_slots.ad_ids:read',
              name: '广告ID管理',
              module: 'growth',
              description: '独立管理广告平台广告ID',
            },
            actions: [
              {
                code: 'growth.ad_slots.ad_ids.edit:write',
                name: '广告ID管理编辑',
                module: 'growth',
              },
            ],
            component: './growth-config/ad-slots',
          },
          {
            path: '/growth-config/ad-slots/types-tab',
            name: 'adTypes',
            access: 'canReadAdTypes',
            permission: {
              code: 'growth.ad_slots.types:read',
              name: '广告类型',
              module: 'growth',
              description: '广告类型管理页',
            },
            actions: [
              {
                code: 'growth.ad_slots.types.create:write',
                name: '新增广告类型按钮',
                module: 'growth',
              },
              {
                code: 'growth.ad_slots.types.edit:write',
                name: '编辑广告类型按钮',
                module: 'growth',
              },
              {
                code: 'growth.ad_slots.types.delete:write',
                name: '删除广告类型按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/ad-slots',
          },
          {
            path: '/growth-config/ad-slots/positions-tab',
            name: 'adSlotPositions',
            access: 'canReadAdSlotPositions',
            permission: {
              code: 'growth.ad_slots.positions:read',
              name: '广告位',
              module: 'growth',
              description: '广告位管理页',
            },
            actions: [
              {
                code: 'growth.ad_slots.positions.create:write',
                name: '新增广告位按钮',
                module: 'growth',
              },
              {
                code: 'growth.ad_slots.positions.edit:write',
                name: '编辑广告位按钮',
                module: 'growth',
              },
              {
                code: 'growth.ad_slots.positions.delete:write',
                name: '删除广告位按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/ad-slots',
          },
          {
            path: '/growth-config/ad-slots/client-positions-tab',
            name: 'adClientPositions',
            access: 'canReadAdClientPositions',
            permission: {
              code: 'growth.ad_slots.client_positions:read',
              name: '客户端位置',
              module: 'growth',
              description: '客户端位置管理页',
            },
            actions: [
              {
                code: 'growth.ad_slots.client_positions.edit:write',
                name: '编辑客户端位置按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/ad-slots',
          },
          {
            path: '/growth-config/ad-slots/placement-settings-tab',
            name: 'adSlotPlacementSettings',
            access: 'canReadAdPlacementSettings',
            permission: {
              code: 'growth.ad_slots.placement_settings:read',
              name: '广告位全局设置',
              module: 'growth',
              description: '广告位全局设置管理页',
            },
            actions: [
              {
                code: 'growth.ad_slots.placement_settings.add_rule:write',
                name: '新增规则按钮',
                module: 'growth',
              },
              {
                code: 'growth.ad_slots.placement_settings.edit_rule:write',
                name: '编辑规则按钮',
                module: 'growth',
              },
              {
                code: 'growth.ad_slots.placement_settings.delete_rule:write',
                name: '删除规则按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/ad-slots',
          },
        ],
      },
      {
        path: '/growth-config/ad-experiments/editor',
        name: 'adExperimentEditor',
        hideInMenu: true,
        component: './growth-config/ad-experiments',
        access: 'canReadAdExperiments',
        permission: {
          code: 'growth.ad_experiments:read',
          name: 'A/B Testing 编辑页',
          module: 'growth',
        },
      },
      {
        path: '/growth-config/ad-experiments',
        name: 'adExperiments',
        component: './growth-config/ad-experiments',
        access: 'canReadAdExperiments',
        permission: [
          {
            code: 'growth.ad_experiments:read',
            name: 'A/B Testing 页面',
            module: 'growth',
          },
          { code: 'growth:read', name: '增长配置兼容访问', module: 'growth' },
        ],
        actions: [
          {
            code: 'growth.ad_experiments.detail:read',
            name: '查看实验详情',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.diff:read',
            name: '预览变体配置差异',
            module: 'growth',
          },
          { code: 'growth.ad_experiments.coverage:read', name: '检查广告配置覆盖', module: 'growth' },
          {
            code: 'growth.ad_experiments.create:write',
            name: '添加 A/B 测试实验',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.update:write',
            name: '更新实验配置或名称',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.delete:write',
            name: '删除广告实验',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.variants.delete:write',
            name: '删除实验分组',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.lifecycle:write',
            name: '启动定时暂停结束实验',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.publish:write',
            name: '发布变体',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.rollback:write',
            name: '回滚已发布变体',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.stats:read',
            name: '查看实验统计',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.manual_assign:write',
            name: '手动固定测试分组',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.column:read',
            name: '实验列表列配置',
            module: 'growth',
          },
          {
            code: 'growth.ad_experiments.reload:read',
            name: '刷新实验列表',
            module: 'growth',
          },
        ],
      },
      {
        path: '/growth-config/reading-events',
        name: '价值和事件上报',
        component: './growth-config/reading-events',
        access: 'canReadReadingEvents',
        permission: [
          {
            code: 'growth.reading_events:read',
            name: '查看事件上报配置',
            module: 'growth',
          },
        ],
        actions: [
          {
            code: 'growth.reading_events.detail:read',
            name: '查看单条事件详情',
            module: 'growth',
          },
          {
            code: 'growth.reading_events.create:write',
            name: '新增事件配置',
            module: 'growth',
          },
          {
            code: 'growth.reading_events.update:write',
            name: '更新事件配置',
            module: 'growth',
          },
          {
            code: 'growth.reading_events.delete:write',
            name: '删除事件配置',
            module: 'growth',
          },
          {
            code: 'growth.reading_events.column:read',
            name: '配置列表列',
            module: 'growth',
          },
          {
            code: 'growth.reading_events.reload:read',
            name: '刷新列表',
            module: 'growth',
          },
        ],
      },
      {
        path: '/growth-config/home-guide',
        name: 'homeGuide',
        access: 'canReadGrowthHomeGuide',
        permission: [
          {
            code: 'growth.home_guide:read',
            name: '查看首页引导位',
            module: 'growth',
            description: '查看首页引导位配置页',
          },
        ],
        actions: [
          {
            code: 'growth.home_guide.detail:read',
            name: '查看首页引导详情',
            module: 'growth',
          },
          {
            code: 'growth.home_guide.column:read',
            name: '配置首页引导列表列',
            module: 'growth',
          },
          {
            code: 'growth.home_guide.update:write',
            name: '编辑首页引导位按钮',
            module: 'growth',
            description: '编辑首页引导位配置页',
          },
          {
            code: 'growth.home_guide.reload:read',
            name: '刷新首页引导位按钮',
            module: 'growth',
          },
          {
            code: 'growth.home_guide.create:write',
            name: '新增首页引导位按钮',
            module: 'growth',
          },
          {
            code: 'growth.home_guide.delete:write',
            name: '删除首页引导位按钮',
            module: 'growth',
          },
          {
            code: 'growth.home_guide.upload:write',
            name: '上传资源按钮',
            module: 'growth',
          },
        ],
        component: './growth-config/home-guide',
      },
      {
        path: '/growth-config/app-decor-profiles',
        redirect: '/growth-config/app-decor/profiles',
        hideInMenu: true,
      },
      {
        path: '/growth-config/app-decor-pools',
        redirect: '/growth-config/app-decor/pools',
        hideInMenu: true,
      },
      {
        path: '/growth-config/app-decor-comic-lists',
        redirect: '/growth-config/app-decor/comic-lists',
        hideInMenu: true,
      },
      {
        path: '/growth-config/app-decor',
        name: 'appDecor',
        access: 'canReadGrowthAppDecor',
        permission: [
          {
            code: 'growth.app_decor:read',
            name: '查看 App 装修',
            module: 'growth',
            description: '查看 App 装修配置页',
          },
        ],
        actions: [
          {
            code: 'growth.app_decor:write',
            name: '编辑 App 装修按钮',
            module: 'growth',
            description: '编辑 App 装修配置页',
          },
          {
            code: 'growth.app_decor.reload:read',
            name: '刷新 App 装修按钮',
            module: 'growth',
          },
          {
            code: 'growth.app_decor.import:write',
            name: '导入装修按钮',
            module: 'growth',
          },
          {
            code: 'growth.app_decor.save:write',
            name: '保存装修按钮',
            module: 'growth',
          },
        ],
        routes: [
          {
            path: '/growth-config/app-decor',
            redirect: '/growth-config/app-decor/profiles',
          },
          {
            path: '/growth-config/app-decor/base',
            name: 'appDecorBase',
            hideInMenu: true,
            permission: [
              {
                code: 'growth.app_decor.base:read',
                name: '基础配置 Tab',
                module: 'growth',
                description: '基础配置标签页',
              },
            ],
            actions: [
              {
                code: 'growth.app_decor.base.save:write',
                name: '保存基础配置按钮',
                module: 'growth',
                description: '基础配置页面按钮',
              },
              {
                code: 'growth.app_decor.base.add_keyword:write',
                name: '新增关键词按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/app-decor/banners',
            name: 'appDecorBanners',
            hideInMenu: true,
            permission: [
              {
                code: 'growth.app_decor.banners:read',
                name: 'Banner 管理 Tab',
                module: 'growth',
                description: 'Banner 管理标签页',
              },
            ],
            actions: [
              {
                code: 'growth.app_decor.banners.create:write',
                name: '新增 Banner 按钮',
                module: 'growth',
                description: 'Banner 管理页面按钮',
              },
              {
                code: 'growth.app_decor.banners.edit:write',
                name: '编辑 Banner 按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.banners.delete:write',
                name: '删除 Banner 按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/app-decor/recommended',
            name: 'appDecorRecommended',
            hideInMenu: true,
            permission: [
              {
                code: 'growth.app_decor.recommended:read',
                name: '首页推荐 Tab',
                module: 'growth',
                description: '首页推荐标签页',
              },
            ],
            actions: [
              {
                code: 'growth.app_decor.recommended.add:write',
                name: '添加推荐漫画按钮',
                module: 'growth',
                description: '首页推荐页面按钮',
              },
              {
                code: 'growth.app_decor.recommended.remove:write',
                name: '移除推荐漫画按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/app-decor/categories',
            name: 'appDecorCategories',
            hideInMenu: true,
            permission: [
              {
                code: 'growth.app_decor.categories:read',
                name: '分类管理 Tab',
                module: 'growth',
                description: '分类管理标签页',
              },
            ],
            actions: [
              {
                code: 'growth.app_decor.categories.create:write',
                name: '新增分类按钮',
                module: 'growth',
                description: '分类管理页面按钮',
              },
              {
                code: 'growth.app_decor.categories.edit:write',
                name: '编辑分类按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.categories.delete:write',
                name: '删除分类按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/app-decor/honeypot-pool',
            name: 'appDecorHoneypotPool',
            hideInMenu: true,
            permission: [
              {
                code: 'growth.app_decor.honeypot_pool:read',
                name: '漫画池 Tab',
                module: 'growth',
                description: '漫画池标签页',
              },
            ],
            actions: [
              {
                code: 'growth.app_decor.honeypot_pool.add:write',
                name: '添加漫画池漫画按钮',
                module: 'growth',
                description: '漫画池页面按钮',
              },
              {
                code: 'growth.app_decor.honeypot_pool.remove:write',
                name: '移除漫画池漫画按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/app-decor/profiles-normal',
            name: 'profilesNormal',
            hideInMenu: true,
            permission: {
              code: 'growth.app_decor.profiles.normal:read',
              name: '普通装修 Tab',
              module: 'growth',
              description: '普通装修方案标签页',
            },
            actions: [
              {
                code: 'growth.app_decor.profiles.normal.save:write',
                name: '保存普通装修按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/app-decor/profiles-channel',
            name: 'profilesChannel',
            hideInMenu: true,
            permission: {
              code: 'growth.app_decor.profiles.channel:read',
              name: '渠道装修 Tab',
              module: 'growth',
              description: '渠道装修方案标签页',
            },
            actions: [
              {
                code: 'growth.app_decor.profiles.channel.save:write',
                name: '保存渠道装修按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/app-decor/profiles',
            name: 'profiles',
            access: 'canReadGrowthAppDecorProfiles',
            permission: {
              code: 'growth.app_decor.profiles:read',
              name: '查看装修方案',
              module: 'growth',
              description: '查看装修方案页面',
            },
            actions: [
              {
                code: 'growth.app_decor.profiles.decor:read',
                name: '查看装修入口按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.profiles.create:write',
                name: '新增装修方案按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.profiles.edit:write',
                name: '编辑装修方案按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.profiles.delete:write',
                name: '删除装修方案按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.profiles.bind:write',
                name: '绑定方案按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/app-decor-profiles',
          },
          {
            path: '/growth-config/app-decor/pools',
            name: 'pools',
            access: 'canReadGrowthAppDecorPools',
            permission: {
              code: 'growth.app_decor.pools:read',
              name: '查看漫画池',
              module: 'growth',
              description: '查看 App 装修漫画池页面',
            },
            actions: [
              {
                code: 'growth.app_decor.pools.create:write',
                name: '新增漫画池按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.pools.edit:write',
                name: '编辑漫画池按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.pools.delete:write',
                name: '删除漫画池按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.pools.manage_comics:write',
                name: '管理池内漫画按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/app-decor-pools',
          },
          {
            path: '/growth-config/app-decor/comic-lists',
            name: 'comicLists',
            access: 'canReadGrowthAppDecorComicLists',
            permission: [
              {
                code: 'growth.app_decor.comic_lists:read',
                name: '漫单管理 Tab',
                module: 'growth',
                description: '漫单管理标签页',
              },
              {
                code: 'growth.app_decor.comic_list_pages:read',
                name: '查看漫单管理',
                module: 'growth',
                description: '查看 App 装修漫单管理页面',
              },
            ],
            actions: [
              {
                code: 'growth.app_decor.comic_lists.create:write',
                name: '新增漫单按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.comic_lists.bind:write',
                name: '绑定已有漫单按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.comic_lists.delete:write',
                name: '删除漫单按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.comic_list_pages.create:write',
                name: '新增漫单按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.comic_list_pages.edit:write',
                name: '编辑漫单按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.comic_list_pages.delete:write',
                name: '删除漫单按钮',
                module: 'growth',
              },
              {
                code: 'growth.app_decor.comic_list_pages.bind:write',
                name: '绑定漫画按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/app-decor-comic-lists',
          },
        ],
      },
      {
        path: '/growth-config/app-decor-editor',
        hideInMenu: true,
        access: 'canReadGrowthAppDecor',
        component: './growth-config/app-decor',
      },
      {
        path: '/growth-config/namespaces',
        name: 'namespaces',
        access: 'canReadGrowthAppDecor',
        permission: {
          code: 'growth.namespaces:read',
          name: '查看命名空间',
          module: 'growth',
          description: '查看 App 装修命名空间页面',
        },
        actions: [
          {
            code: 'growth.namespaces.create:write',
            name: '新增命名空间按钮',
            module: 'growth',
          },
          {
            code: 'growth.namespaces.edit:write',
            name: '编辑命名空间按钮',
            module: 'growth',
          },
          {
            code: 'growth.namespaces.delete:write',
            name: '删除命名空间按钮',
            module: 'growth',
          },
        ],
        component: './growth-config/namespaces',
      },
      {
        path: '/growth-config/feedbacks',
        name: 'feedbacks',
        access: 'canReadGrowthFeedbacks',
        permission: [
          {
            code: 'growth.feedbacks:read',
            name: '查看用户反馈',
            module: 'growth',
            description: '查看增长模块下的用户反馈',
          },
        ],
        actions: [
          {
            code: 'growth.feedbacks:write',
            name: '处理用户反馈按钮',
            module: 'growth',
            description: '更新反馈状态并执行阅读器反馈的运维操作',
          },
          {
            code: 'growth.feedbacks.refresh:read',
            name: '刷新反馈按钮',
            module: 'growth',
          },
        ],
        routes: [
          {
            path: '/growth-config/feedbacks',
            redirect: '/growth-config/feedbacks/setting',
          },
          {
            path: '/growth-config/feedbacks/setting-meta',
            name: 'feedbackSettings',
            hideInMenu: true,
            permission: [
              {
                code: 'growth.feedbacks.setting:read',
                name: '反馈配置 Tab',
                module: 'growth',
                description: '反馈配置页标签页',
              },
            ],
            actions: [
              {
                code: 'growth.feedbacks.setting.save:write',
                name: '保存反馈配置按钮',
                module: 'growth',
                description: '反馈配置页按钮',
              },
              {
                code: 'growth.feedbacks.setting:write',
                name: '编辑反馈配置按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/feedbacks/reader-meta',
            name: 'feedbackReader',
            hideInMenu: true,
            permission: [
              {
                code: 'growth.feedbacks.reader:read',
                name: '阅读器反馈 Tab',
                module: 'growth',
                description: '阅读器反馈页标签页',
              },
            ],
            actions: [
              {
                code: 'growth.feedbacks.reader.resolve:write',
                name: '处理阅读器反馈按钮',
                module: 'growth',
                description: '阅读器反馈页按钮',
              },
              {
                code: 'growth.feedbacks.reader:write',
                name: '编辑阅读器反馈按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.reader.preview:read',
                name: '查看反馈图片按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.reader.filter_user:read',
                name: '按用户筛选按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.reader.export:read',
                name: '导出阅读器反馈按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/feedbacks/summary-meta',
            name: 'feedbackSummary',
            hideInMenu: true,
            permission: [
              {
                code: 'growth.feedbacks.summary:read',
                name: '反馈汇总 Tab',
                module: 'growth',
                description: '反馈汇总标签页',
              },
            ],
            actions: [
              {
                code: 'growth.feedbacks.summary.search:read',
                name: '搜索反馈汇总按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.summary.reset:read',
                name: '重置反馈汇总按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.summary.batch_resolve:write',
                name: '批量处理反馈按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.summary.export:read',
                name: '导出反馈汇总按钮',
                module: 'growth',
              },
            ],
          },
          {
            path: '/growth-config/feedbacks/setting',
            name: 'settings',
            access: 'canReadGrowthFeedbacks',
            permission: {
              code: 'growth.feedbacks.settings_page:read',
              name: '查看配置页反馈',
              module: 'growth',
              description: '查看配置页反馈页面',
            },
            actions: [
              {
                code: 'growth.feedbacks.settings_page.user_settings.detail:read',
                name: '查看用户设置',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.settings_page.user_settings.update:write',
                name: '修改用户设置',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.settings_page.save:write',
                name: '保存配置页反馈按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/feedbacks-setting',
          },
          {
            path: '/growth-config/feedbacks/reader',
            name: 'readerUser',
            access: 'canReadGrowthFeedbacks',
            permission: {
              code: 'growth.feedbacks.reader_page:read',
              name: '查看阅读器反馈',
              module: 'growth',
              description: '查看阅读器反馈页面',
            },
            actions: [
              {
                code: 'growth.feedbacks.reader_page.user_settings.detail:read',
                name: '查看用户设置',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.reader_page.user_settings.update:write',
                name: '修改用户设置',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.reader_page.resolve:write',
                name: '处理反馈按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.reader_page.preview:read',
                name: '预览反馈图片按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.reader_page.export:read',
                name: '导出阅读器反馈 CSID 按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.reader_page.bug_report:write',
                name: '提交阅读器 BUG 按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/feedbacks-reader',
          },
          {
            path: '/growth-config/feedbacks/summary',
            name: 'summary',
            access: 'canReadGrowthFeedbacks',
            permission: {
              code: 'growth.feedbacks.summary_page:read',
              name: '查看反馈汇总',
              module: 'growth',
              description: '查看反馈汇总页面',
            },
            actions: [
              {
                code: 'growth.feedbacks.summary_page.search:read',
                name: '搜索反馈汇总按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.summary_page.batch_resolve:write',
                name: '批量处理反馈按钮',
                module: 'growth',
              },
              {
                code: 'growth.feedbacks.summary_page.export:read',
                name: '导出反馈汇总按钮',
                module: 'growth',
              },
            ],
            component: './growth-config/feedbacks-summary',
          },
          {
            path: '/growth-config/feedbacks/reader-comics',
            redirect: '/growth-config/feedbacks/reader',
            hideInMenu: true,
          },
        ],
      },
    ],
  },
  {
    path: '/config-management',
    name: 'configManagement',
    icon: 'setting',
    access: 'canReadConfigManagement',
    permission: {
      code: 'config_management.global_config:read',
      name: '进入全局配置',
      module: 'config_management',
      description: '进入全局配置主菜单',
    },
    routes: [
      {
        path: '/config-management/global',
        redirect: '/config-management/feature-flags',
        hideInMenu: true,
      },
      {
        path: '/config-management/feature-flags',
        name: 'featureFlags',
        access: 'canReadConfigManagementFeatureFlags',
        permission: {
          code: 'config_management.global_config:read',
          name: '查看 Feature Flag',
          module: 'config_management',
          description: '查看并管理 Feature Flag 和远程 JSON 配置',
        },
        actions: [
          {
            code: 'config_management.global_config.update:write',
            name: '保存 Feature Flag 按钮',
            module: 'config_management',
            description: '保存 Feature Flag 和远程 JSON 配置',
          },
        ],
        component: './config-management/global-config',
      },
      {
        path: '/config-management/lab',
        name: 'lab',
        access: 'canReadConfigManagementLab',
        permission: {
          code: 'config_management.lab:read',
          name: '查看实验室',
          module: 'config_management',
          description: '查看实验室配置',
        },
        component: './config-management/lab',
      },
    ],
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    icon: 'tool',
    access: 'canAccessMaintenance',
    permission: {
      code: 'maintenance:read',
      name: '进入维护管理',
      module: 'maintenance',
      description: '进入维护管理主菜单',
    },
    routes: [
      {
        path: '/maintenance',
        redirect: '/maintenance/reader-bugs',
      },
      {
        path: '/maintenance/reader-bugs',
        name: 'readerBugs',
        access: 'canReadMaintenanceReaderBugs',
        permission: {
          code: 'maintenance.reader_bugs:read',
          name: '查看 BUG 管理',
          module: 'maintenance',
          description: '查看阅读器 BUG 处理记录',
        },
        actions: [
          {
            code: 'maintenance.reader_bugs.refresh:read',
            name: '刷新 BUG 列表按钮',
            module: 'maintenance',
          },
          {
            code: 'maintenance.reader_bugs.detail:read',
            name: '查看 BUG 详情',
            module: 'maintenance',
          },
          {
            code: 'maintenance.reader_bugs.resolve:write',
            name: '解决 BUG 按钮',
            module: 'maintenance',
          },
        ],
        component: './maintenance/reader-bugs',
      },
    ],
  },
  {
    path: '/system',
    name: 'system',
    icon: 'team',
    access: 'canAccessSystem',
    permission: {
      code: 'system:read',
      name: '进入系统管理',
      module: 'system',
      description: '进入系统管理主菜单',
    },
    routes: [
      {
        path: '/system',
        redirect: '/system/users',
      },
      {
        path: '/system/users',
        name: 'users',
        access: 'canReadUsers',
        permission: [
          {
            code: 'users:read',
            name: '查看用户',
            module: 'users',
            description: '查看后台用户信息',
          },
        ],
        actions: [
          {
            code: 'users:write',
            name: '编辑用户按钮',
            module: 'users',
            description: '创建、修改、删除后台用户并分配账号组',
          },
          { code: 'users.create:write', name: '新增用户按钮', module: 'users' },
          { code: 'users.delete:write', name: '删除用户按钮', module: 'users' },
          {
            code: 'users.reset_mfa:write',
            name: '重置 MFA 按钮',
            module: 'users',
          },
          {
            code: 'users.tenant_access:read',
            name: '查看产品权限',
            module: 'users',
          },
          {
            code: 'users.tenant_access:write',
            name: '管理产品权限',
            module: 'users',
          },
        ],
        component: './system/users',
      },
      {
        path: '/system/tenants',
        name: 'tenants',
        access: 'canReadTenants',
        permission: {
          code: 'tenants:read',
          name: '查看租户',
          module: 'system',
          description: '查看和管理基础库租户配置',
        },
        actions: [
          {
            code: 'tenants:write',
            name: '管理租户按钮',
            module: 'system',
            description: '新增、修改和停用基础库租户',
          },
        ],
        component: './system/tenants',
      },
      {
        path: '/system/groups',
        name: 'groups',
        access: 'canReadGroups',
        permission: [
          {
            code: 'groups:read',
            name: '查看账号组',
            module: 'groups',
            description: '查看账号组和授权关系',
          },
        ],
        actions: [
          {
            code: 'groups:write',
            name: '编辑账号组按钮',
            module: 'groups',
            description: '创建、修改、删除账号组',
          },
          {
            code: 'groups.permissions:write',
            name: '编辑账号组权限按钮',
            module: 'groups',
            description: '更新账号组授权关系',
          },
          {
            code: 'groups.create:write',
            name: '新增账号组按钮',
            module: 'groups',
          },
          {
            code: 'groups.delete:write',
            name: '删除账号组按钮',
            module: 'groups',
          },
        ],
        component: './system/groups',
      },
      {
        path: '/system/groups/:id/permissions',
        name: 'groupPermissions',
        hideInMenu: true,
        access: 'canReadGroups',
        permission: {
          code: 'groups.permissions_page:read',
          name: '查看客户端权限',
          module: 'groups',
          description: '查看账号组客户端权限设置页',
        },
        actions: [
          {
            code: 'groups.permissions_page.sync:write',
            name: '同步权限目录按钮',
            module: 'groups',
          },
          {
            code: 'groups.permissions_page.save:write',
            name: '保存权限按钮',
            module: 'groups',
          },
        ],
        component: './system/groups/permissions',
      },
      {
        path: '/system/permissions',
        name: 'permissions',
        access: 'canReadPermissions',
        permission: {
          code: 'permissions:read',
          name: '查看权限目录',
          module: 'permissions',
          description: '查看和同步系统权限定义',
        },
        actions: [
          {
            code: 'permissions.sync:write',
            name: '同步内置权限按钮',
            module: 'permissions',
          },
        ],
        component: './system/permissions',
      },
      {
        path: '/system/action-logs',
        name: 'actionLogs',
        access: 'canReadActionLogs',
        permission: {
          code: 'audit_logs:read',
          name: '查看操作审计',
          module: 'audit_logs',
          description: '查看后台操作审计日志',
        },
        component: './system/action-logs',
      },
      {
        path: '/system/flower',
        name: 'flower',
        access: 'canReadSystemFlower',
        permission: {
          code: 'system.flower:read',
          name: '查看任务监控',
          module: 'system',
          description: '查看任务监控页面',
        },
        actions: [
          {
            code: 'system.flower.open:read',
            name: '打开任务监控按钮',
            module: 'system',
          },
          {
            code: 'system.flower.refresh:read',
            name: '刷新任务监控按钮',
            module: 'system',
          },
        ],
        component: './system/flower',
      },
      {
        path: '/system/version-management',
        redirect: '/version-management/packages',
        hideInMenu: true,
      },
      {
        path: '/system/version-management/version',
        redirect: '/version-management/packages',
        hideInMenu: true,
      },
      {
        path: '/system/version-management/ips',
        redirect: '/version-management/ips',
        hideInMenu: true,
      },
      {
        path: '/system/version-management/packages',
        redirect: '/version-management/packages',
        hideInMenu: true,
      },
      {
        path: '/system/cache',
        name: 'cache',
        access: 'canReadSystemCache',
        permission: {
          code: 'system.cache:read',
          name: '查看缓存管理',
          module: 'system',
          description: '查看缓存管理页面',
        },
        actions: [
          {
            code: 'system.cache.clear:write',
            name: '清理缓存按钮',
            module: 'system',
          },
        ],
        component: './system/cache',
      },
      {
        path: '/system/encrypted-domains',
        name: 'encryptedDomains',
        access: 'canReadSystemEncryptedDomains',
        permission: {
          code: 'system.encrypted_domains:read',
          name: '查看混淆域名',
          module: 'system',
          description: '查看混淆域名页面',
        },
        actions: [
          {
            code: 'system.encrypted_domains.refresh:read',
            name: '刷新混淆域名按钮',
            module: 'system',
          },
        ],
        component: './system/encrypted-domains',
      },
      {
        path: '/system/policy-gateway-test-routes',
        name: 'policyGatewayTestRoutes',
        access: 'canReadSystemPolicyGatewayTestRoutes',
        permission: {
          code: 'system.policy_gateway_test_routes:read',
          name: '查看用户转发测试路由',
          module: 'system',
          description: '查看用户转发测试路由页面',
        },
        actions: [
          {
            code: 'system.policy_gateway_test_routes.create:write',
            name: '新增测试路由按钮',
            module: 'system',
          },
          {
            code: 'system.policy_gateway_test_routes.edit:write',
            name: '编辑测试路由按钮',
            module: 'system',
          },
          {
            code: 'system.policy_gateway_test_routes.delete:write',
            name: '删除测试路由按钮',
            module: 'system',
          },
          {
            code: 'system.policy_gateway_test_routes.refresh:read',
            name: '刷新测试路由按钮',
            module: 'system',
          },
        ],
        component: './system/policy-gateway-test-routes',
        hideInMenu: true,
      },
    ],
  },
  {
    path: '/admin',
    name: 'admin',
    icon: 'crown',
    access: 'canAdmin',
    hideInMenu: true,
    routes: [
      {
        path: '/admin',
        redirect: '/admin/sub-page',
      },
      {
        path: '/admin/sub-page',
        name: 'sub-page',
        component: './Admin',
      },
    ],
  },
  {
    name: 'list.table-list',
    icon: 'table',
    path: '/list',
    component: './table-list',
    hideInMenu: true,
  },
  {
    path: '/',
    redirect: '/welcome',
  },
  {
    path: '*',
    layout: false,
    component: './404',
  },
];
